/**
 * @file OverviewTile.tsx
 * @description Плитка показателя «Обзора» платформы (phase19.29): значок,
 * подпись, значение и пояснение; плитка целиком — ссылка в свой раздел.
 *
 * Состояния источника — из `OverviewSource`: загрузка — скелет, ошибка —
 * понятный текст и «Повторить» (тогда плитка не ссылка: кнопка внутри
 * ссылки недопустима), готово — содержимое из `children`. Без права плитку
 * не рисует родитель. Наведение — `card-interactive` (подъём, тень, линия).
 */

import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { Button } from "../../ui/button";
import { Skeleton } from "../../ui/skeleton";
import { cn } from "@/lib/utils";

interface Props {
  to: string;
  icon: LucideIcon;
  label: string;
  status: "loading" | "error" | "ready";
  errorMessage?: string;
  onRetry?: () => void;
  /** Значение и пояснение — когда данные готовы. */
  children?: ReactNode;
}

/** Общая рамка плитки: белая карточка с тонкой линией (доска бренда). */
const TILE_CLASS =
  "flex h-full min-h-32 flex-col gap-3 rounded-xl bg-card p-5 ring-1 ring-outline-variant";

export function OverviewTile({ to, icon: Icon, label, status, errorMessage, onRetry, children }: Props) {
  const head = (
    <div className="flex items-center gap-3">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
        <Icon className="size-4" aria-hidden />
      </span>
      <span className="text-sm font-medium text-on-surface-variant">{label}</span>
    </div>
  );

  if (status === "error") {
    return (
      <div className={TILE_CLASS} role="group" aria-label={label}>
        {head}
        <p className="text-sm text-on-surface">{errorMessage}</p>
        <Button variant="soft" className="mt-auto self-start" onClick={onRetry}>
          Повторить
        </Button>
      </div>
    );
  }

  return (
    <Link
      to={to}
      aria-busy={status === "loading"}
      className={cn(
        TILE_CLASS,
        // Подъём, тень и синяя линия при наведении — общий класс index.css.
        "card-interactive outline-none",
      )}
    >
      {head}
      {status === "loading" ? (
        <div className="flex flex-col gap-2" aria-label="Загрузка">
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-4 w-40" />
        </div>
      ) : (
        children
      )}
    </Link>
  );
}

/**
 * Значение плитки + пояснение. `metric` — числа и короткие значения
 * (роль «показатель», 24/28px); `text` — текстовое значение («Копий ещё не
 * было»): роль «заголовок секции» 18/600, чтобы не переносилось тяжело.
 */
export function TileValue({
  value,
  hint,
  kind = "metric",
}: {
  value: ReactNode;
  hint?: ReactNode;
  kind?: "metric" | "text";
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className={cn(kind === "metric" ? "type-metric" : "type-section-title", "text-on-surface")}>
        {value}
      </div>
      {hint ? <div className="text-sm text-on-surface-variant">{hint}</div> : null}
    </div>
  );
}
